import { createPortal } from 'react-dom';
import { useEffect, useRef, useState } from 'react';
import { WaterStage } from './WaterStage';
import { DemoCursor } from './DemoCursor';
import { DEMO_STEPS } from './demoScript';

interface OnboardingDemoProps {
  firstProjectId?: string;
  onComplete: () => void;
  navigate: (to: string) => void;
}

function applyLift(targetId: string, tiltDeg: number) {
  const el = document.querySelector(`[data-demo-id="${targetId}"]`) as HTMLElement | null;
  if (!el) return;
  el.style.transition = 'transform 0.65s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.65s ease, border-radius 0.3s ease';
  el.style.transform = `translateY(-18px) perspective(1200px) rotateY(${tiltDeg}deg) scale(1.03)`;
  el.style.boxShadow = '0 32px 72px rgba(0,0,0,0.18), 0 8px 28px rgba(57,148,255,0.14)';
  el.style.borderRadius = '1rem';
  el.style.position = 'relative';
  el.style.zIndex = '40';
  el.style.overflow = 'hidden';
}

function removeLift(targetId: string) {
  const el = document.querySelector(`[data-demo-id="${targetId}"]`) as HTMLElement | null;
  if (!el) return;
  el.style.transform = '';
  el.style.boxShadow = '';
  el.style.borderRadius = '';
  el.style.overflow = '';
  el.style.position = '';
  el.style.zIndex = '';
  setTimeout(() => { el.style.transition = ''; }, 700);
}

export function OnboardingDemo({ firstProjectId, onComplete, navigate }: OnboardingDemoProps) {
  const [cursorTarget, setCursorTarget] = useState<string | undefined>(undefined);
  const [narration, setNarration] = useState('');
  const [typingDurationMs, setTypingDurationMs] = useState(8000);
  const [waterVisible, setWaterVisible] = useState(false);

  // Mutable refs so async loop always reads latest values without re-running the effect
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const cancelledRef = useRef(false);
  const currentLiftRef = useRef<string | undefined>(undefined);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);
  const pendingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    cancelledRef.current = false;

    const steps = DEMO_STEPS.map((s) => ({
      ...s,
      route: s.route === ':project'
        ? (firstProjectId ? `/projects/${firstProjectId}` : '/')
        : s.route,
    }));

    const sleep = (ms: number) =>
      new Promise<void>((resolve) => {
        pendingTimeoutRef.current = setTimeout(resolve, ms);
      });

    async function run() {
      setWaterVisible(true);
      await sleep(900);

      for (const step of steps) {
        if (cancelledRef.current) return;

        navigateRef.current(step.route);
        await sleep(step.renderDelay ?? 700);
        if (cancelledRef.current) return;

        if (step.liftTarget) {
          applyLift(step.liftTarget, step.liftTilt ?? 3);
          currentLiftRef.current = step.liftTarget;
        }

        setCursorTarget(step.cursorTarget ?? step.liftTarget);
        setNarration(step.narration);
        setTypingDurationMs(step.durationMs);

        // Play audio; advance on 'ended' or durationMs timeout, whichever comes first.
        // Audio errors (e.g. 404) do NOT short-circuit — the durationMs fallback always fires.
        if (currentAudioRef.current) currentAudioRef.current.pause();
        const audio = new Audio(step.audioSrc);
        currentAudioRef.current = audio;

        await new Promise<void>((resolve) => {
          let done = false;
          const finish = () => { if (!done) { done = true; resolve(); } };
          pendingTimeoutRef.current = setTimeout(finish, step.durationMs);
          audio.addEventListener('ended', finish, { once: true });
          // Suppress error short-circuit — let durationMs timeout drive advancement
          audio.addEventListener('error', () => { /* no-op */ }, { once: true });
          void audio.play().catch(() => { /* no-op — timeout will advance */ });
        });

        if (cancelledRef.current) return;

        if (currentLiftRef.current) {
          removeLift(currentLiftRef.current);
          currentLiftRef.current = undefined;
        }

        await sleep(700);
        if (cancelledRef.current) return;
      }

      // All steps done — return home and complete
      navigateRef.current('/');
      setCursorTarget(undefined);
      setNarration('');
      setWaterVisible(false);
      await sleep(900);
      if (!cancelledRef.current) onCompleteRef.current();
    }

    void run();

    return () => {
      cancelledRef.current = true;
      if (pendingTimeoutRef.current) clearTimeout(pendingTimeoutRef.current);
      if (currentLiftRef.current) removeLift(currentLiftRef.current);
      if (currentAudioRef.current) { currentAudioRef.current.pause(); currentAudioRef.current = null; }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDismiss = () => {
    cancelledRef.current = true;
    if (pendingTimeoutRef.current) clearTimeout(pendingTimeoutRef.current);
    if (currentLiftRef.current) { removeLift(currentLiftRef.current); currentLiftRef.current = undefined; }
    if (currentAudioRef.current) { currentAudioRef.current.pause(); currentAudioRef.current = null; }
    navigateRef.current('/');
    setCursorTarget(undefined);
    setNarration('');
    setWaterVisible(false);
    setTimeout(() => onCompleteRef.current(), 900);
  };

  return createPortal(
    <>
      <WaterStage
        text={narration}
        typingDurationMs={typingDurationMs}
        isVisible={waterVisible}
        onDismiss={handleDismiss}
      />
      <DemoCursor targetId={cursorTarget} visible={waterVisible} />
    </>,
    document.body,
  );
}
